import React, { useMemo, useState } from "react";

import { Search } from "lucide-react";

import {
  BUCKET_ORDER,
  bucketOf,
  type DraftPlan,
  draftPlan,
  isEditedFromProposal,
  MAX_STEPS,
  planCardCta,
  planErrors,
  STATUS_META,
  USER_AWARENESS_NOTE,
} from "./actionPlans";
import CardPreview from "./CardPreview";
import { CtaEditor } from "./CtaPanel";
import Drawer from "./Drawer";
import { sampleFields } from "./guides";
import { draftRuntime } from "./runtime";
import { SignalSettingsContent } from "./SignalDetailDrawer";
import { draftTargets } from "./targets";
import {
  draftParameterValues,
  draftTier,
  type ActionBucket,
  type ActionOwner,
  type ActionPlan,
  type ActionPlanStatus,
  type ActionPlanStep,
  type SignalAdminDraft,
  type SignalAdminRegistry,
  type SignalDefinition,
  type SignalTier,
  IMPLEMENTATION_LABEL,
  RUNTIME_META,
  TIER_META,
} from "./types";
import Button from "../../Components/Button";
import { SignalGuideContent } from "../../Pages/MyPortfolioAccounts/SignalGuideModal";
import type { SignalGuide } from "../../Store/Reducers/SignalFeedSlice";

interface SignalActionsPanelProps {
  registry: SignalAdminRegistry;
  draft: SignalAdminDraft;
  /** The applied config's reviewed plans, to mark staged rows. */
  appliedPlans: Record<string, ActionPlan>;
  onDraftChange: (next: SignalAdminDraft) => void;
  /** Save changes-requested reviews for every admin (no Apply needed —
   * they change nothing users see). Rejects with a readable message. */
  onSaveReviews: (reviews: Record<string, ActionPlan>) => Promise<void>;
}

type GroupBy = "bucket" | "family";

const OWNER_CLASS: Record<ActionOwner, string> = {
  you: "bg-brand-faint text-brand-dark ring-brand/20",
  exec_sponsor: "bg-violet-50 text-violet-700 ring-violet-600/20",
  account_team: "bg-sky-50 text-sky-700 ring-sky-600/20",
  customer: "bg-orange-50 text-orange-700 ring-orange-600/20",
  approver: "bg-slate-100 text-slate-700 ring-slate-500/20",
};

const pill =
  "inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-semibold ring-1 ring-inset";

const inputClass =
  "w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-[12.5px] text-gray-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand";

const selectClass =
  "h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-[12.5px] text-gray-800 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";

const SIZE_LABEL = { single: "Single", multiple: "Multiple" } as const;

const blankStep = (): ActionPlanStep => ({
  title: "",
  owner: "you",
  target: "",
  detail: "",
});

type Row = { definition: SignalDefinition; plan: DraftPlan };

const SignalActionsPanel: React.FC<SignalActionsPanelProps> = ({
  registry,
  draft,
  appliedPlans,
  onDraftChange,
  onSaveReviews,
}) => {
  const [bucketFilter, setBucketFilter] = useState<ActionBucket | null>(null);
  const [query, setQuery] = useState("");
  const [family, setFamily] = useState("");
  const [layer, setLayer] = useState("");
  const [status, setStatus] = useState("");
  const [runtime, setRuntime] = useState("");
  const [review, setReview] = useState<"" | ActionPlanStatus>("");
  const [groupBy, setGroupBy] = useState<GroupBy>("bucket");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [openTab, setOpenTab] = useState<DrawerTab>("settings");
  /** Row click opens Signal settings; Review/Edit open the Action plan. */
  const openSignal = (id: string, tab: DrawerTab) => {
    setOpenTab(tab);
    setEditingId(id);
  };

  const targets = useMemo(
    () => draftTargets(registry, draft),
    [registry, draft]
  );
  const targetById = new Map(targets.map(t => [t.id, t]));
  const layerLabel = (id: string) =>
    registry.layers.find(l => l.id === id)?.label ?? id;

  const rows = useMemo(
    () =>
      registry.definitions
        .map(definition => ({
          definition,
          plan: draftPlan(registry, draft, appliedPlans, definition.id),
        }))
        .filter((row): row is Row => row.plan !== null),
    [registry, draft, appliedPlans]
  );

  const counts = useMemo(() => {
    const out = {} as Record<ActionBucket, { total: number; approved: number }>;
    BUCKET_ORDER.forEach(b => {
      out[b] = { total: 0, approved: 0 };
    });
    rows.forEach(({ plan }) => {
      out[plan.bucket].total += 1;
      if (plan.status === "approved") out[plan.bucket].approved += 1;
    });
    return out;
  }, [rows]);

  const stagePlan = (definitionId: string, plan: ActionPlan | null) => {
    const next = { ...(draft.action_plans ?? {}) };
    if (plan) next[definitionId] = plan;
    else delete next[definitionId];
    onDraftChange({ ...draft, action_plans: next });
  };

  /** Changes-requested reviews still only in this browser's draft, that can
   * save on their own (a live approved plan waits for Apply). */
  const localReviews = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(draft.action_plans ?? {}).filter(
          ([id, plan]) =>
            plan.status === "changes_requested" &&
            appliedPlans[id]?.status !== "approved" &&
            JSON.stringify(plan) !== JSON.stringify(appliedPlans[id] ?? null)
        )
      ),
    [draft, appliedPlans]
  );
  const localReviewCount = Object.keys(localReviews).length;
  const [savingLocal, setSavingLocal] = useState(false);
  const [saveLocalError, setSaveLocalError] = useState("");
  const saveLocalReviews = async () => {
    setSavingLocal(true);
    setSaveLocalError("");
    try {
      await onSaveReviews(localReviews);
    } catch (error) {
      setSaveLocalError(
        typeof error === "string" ? error : "Couldn't save these reviews."
      );
    } finally {
      setSavingLocal(false);
    }
  };

  /** Save one review straight away. It is staged first, so the draft
   * (replayed onto the new version) carries the same plan, not an older one. */

  const approve = (definitionId: string, plan: DraftPlan) => {
    const approved: ActionPlan = {
      steps: plan.steps.map(s => ({ ...s })),
      rationale: plan.rationale,
      status: "approved",
      note: "",
      awareness: plan.awareness,
    };
    if (planErrors(approved).length) {
      openSignal(definitionId, "plan");
      return;
    }
    stagePlan(definitionId, approved);
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(({ definition: d, plan }) => {
      if (bucketFilter && plan.bucket !== bucketFilter) return false;
      if (review && plan.status !== review) return false;
      if (family && d.family !== family) return false;
      if (layer && !d.layers.includes(layer)) return false;
      if (status && d.implementation_status !== status) return false;
      if (runtime && draftRuntime(d, draft) !== runtime) return false;
      if (
        q &&
        ![d.id, d.title, d.trigger, ...plan.steps.map(st => st.title)]
          .join(" ")
          .toLowerCase()
          .includes(q)
      )
        return false;
      return true;
    });
  }, [
    rows,
    bucketFilter,
    review,
    family,
    layer,
    status,
    runtime,
    query,
    draft,
  ]);

  const groups =
    groupBy === "bucket"
      ? BUCKET_ORDER.map(b => ({
          id: b,
          label: registry.action_plan_buckets[b]?.label ?? b,
          hint: registry.action_plan_buckets[b]?.hint ?? "",
          items: visible.filter(r => r.plan.bucket === b),
        }))
      : Object.entries(registry.families).map(([id, label]) => ({
          id,
          label,
          hint: "",
          items: visible.filter(r => r.definition.family === id),
        }));
  const shownGroups = groups.filter(g => g.items.length > 0);
  const filtersActive = Boolean(
    query || family || layer || status || runtime || review || bucketFilter
  );
  const clearFilters = () => {
    setQuery("");
    setFamily("");
    setLayer("");
    setStatus("");
    setRuntime("");
    setReview("");
    setBucketFilter(null);
  };
  const editing = rows.find(r => r.definition.id === editingId) ?? null;

  const targetText = (step: ActionPlanStep) => {
    if (!step.target) return null;
    const record = targetById.get(step.target);
    return {
      label: record?.label ?? step.target,
      available: record?.available ?? false,
    };
  };

  const matrixCell = (size: "single" | "multiple", reliance: string) => {
    const bucket = `${size}_${reliance}` as ActionBucket;
    const meta = registry.action_plan_buckets[bucket];
    const c = counts[bucket];
    const active = bucketFilter === bucket;
    return (
      <button
        key={bucket}
        type="button"
        aria-pressed={active}
        onClick={() => setBucketFilter(active ? null : bucket)}
        className={`flex min-w-0 items-center gap-3 rounded-lg border px-3.5 py-2.5 text-left transition-colors ${
          active
            ? "border-brand bg-brand-faint"
            : "border-gray-200 bg-white hover:border-brand/50"
        }`}
      >
        <span className="text-[20px] font-bold tabular-nums text-gray-900">
          {c.total}
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="text-[12px] font-semibold text-gray-800">
            {meta?.label}
          </span>
          <span className="text-[11px] font-semibold tabular-nums text-emerald-700">
            {c.approved} of {c.total} approved
          </span>
        </span>
      </button>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <span className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-brand-dark">
          All signals in one place
        </span>
        <h3 className="text-[15px] font-bold text-gray-900">Registry</h3>
      </div>
      <p className="m-0 max-w-3xl text-[12.5px] text-gray-600">
        Every signal, what it takes to close it, and its runtime state.{" "}
        <strong>Single</strong> or <strong>multiple</strong> is how many actions
        the user who sees the card takes; <strong>dependent</strong> means
        someone else also has a step. Click a row to review its plan; open its
        settings there for dependencies, parameters and on/off. Approvals go out
        with the next Apply.
      </p>

      {localReviewCount > 0 && (
        <div
          role="status"
          className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5"
        >
          <div className="min-w-0 flex-1 text-[12.5px] text-amber-900">
            <strong>
              {localReviewCount} review note{localReviewCount === 1 ? "" : "s"}
            </strong>{" "}
            {localReviewCount === 1 ? "is" : "are"} only in this browser. Save{" "}
            {localReviewCount === 1 ? "it" : "them"} so every admin can see{" "}
            {localReviewCount === 1 ? "it" : "them"} — this doesn&apos;t change
            what users see.
            {saveLocalError && (
              <span className="mt-1 block text-red-700">{saveLocalError}</span>
            )}
          </div>
          <Button
            variant="primary"
            disabled={savingLocal}
            onClick={() => void saveLocalReviews()}
          >
            {savingLocal ? "Saving…" : "Save for every admin"}
          </Button>
        </div>
      )}

      <div className="grid grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)] gap-2">
        <span />
        <span className="px-1 text-[10.5px] font-bold uppercase tracking-[0.07em] text-gray-400">
          Independent · only the user acts
        </span>
        <span className="px-1 text-[10.5px] font-bold uppercase tracking-[0.07em] text-gray-400">
          Dependent · someone else also acts
        </span>
        {(["single", "multiple"] as const).map(size => (
          <React.Fragment key={size}>
            <span className="self-center pr-2 text-[10.5px] font-bold uppercase tracking-[0.07em] text-gray-400">
              {SIZE_LABEL[size]}
            </span>
            {matrixCell(size, "independent")}
            {matrixCell(size, "dependent")}
          </React.Fragment>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1 sm:flex-none">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            aria-hidden
          />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search name, id, trigger or action…"
            className="h-9 w-full rounded-lg border border-gray-300 pl-8 pr-3 text-[12.5px] text-gray-900 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 sm:w-[240px]"
          />
        </div>
        <select
          aria-label="Review"
          value={review}
          onChange={e => setReview(e.target.value as "" | ActionPlanStatus)}
          className={selectClass}
        >
          <option value="">All reviews</option>
          {(Object.keys(STATUS_META) as ActionPlanStatus[]).map(st => (
            <option key={st} value={st}>
              {STATUS_META[st].label}
            </option>
          ))}
        </select>
        <select
          aria-label="Family"
          value={family}
          onChange={e => setFamily(e.target.value)}
          className={selectClass}
        >
          <option value="">All families</option>
          {Object.entries(registry.families).map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
        <select
          aria-label="Data layer"
          value={layer}
          onChange={e => setLayer(e.target.value)}
          className={selectClass}
        >
          <option value="">All layers</option>
          {registry.layers.map(l => (
            <option key={l.id} value={l.id}>
              {l.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Implementation"
          value={status}
          onChange={e => setStatus(e.target.value)}
          className={selectClass}
        >
          <option value="">All statuses</option>
          {Object.entries(IMPLEMENTATION_LABEL).map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
        <select
          aria-label="Runtime"
          value={runtime}
          onChange={e => setRuntime(e.target.value)}
          className={selectClass}
        >
          <option value="">All runtime states</option>
          {Object.entries(RUNTIME_META).map(([id, meta]) => (
            <option key={id} value={id}>
              {meta.label}
            </option>
          ))}
        </select>
        {filtersActive && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs font-semibold text-brand hover:underline"
          >
            Clear filters
          </button>
        )}
        <div
          className="ml-auto inline-flex overflow-hidden rounded-full border border-gray-300"
          role="group"
          aria-label="Group by"
        >
          {(["bucket", "family"] as GroupBy[]).map(g => (
            <button
              key={g}
              type="button"
              aria-pressed={groupBy === g}
              onClick={() => setGroupBy(g)}
              className={`px-3 py-1 text-[12px] font-semibold ${
                groupBy === g
                  ? "bg-brand text-white"
                  : "bg-white text-gray-600 hover:text-gray-900"
              }`}
            >
              By {g}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-500">
          {visible.length} of {rows.length} signals
        </span>
      </div>

      {shownGroups.length === 0 && (
        <div className="rounded-xl border border-gray-200 bg-white px-5 py-10 text-center text-[12.5px] text-gray-500">
          No signals match these filters.
        </div>
      )}

      {shownGroups.map(group => (
        <section
          key={group.id}
          className="overflow-hidden rounded-xl border border-gray-200 bg-white"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-100 px-4 py-2.5">
            <h4 className="m-0 text-[13px] font-bold text-gray-900">
              {group.label}
              {group.hint && (
                <span className="ml-2 text-[12px] font-normal text-gray-500">
                  {group.hint}
                </span>
              )}
            </h4>
            <span className="text-[11.5px] text-gray-400">
              {group.items.length} signal{group.items.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] table-fixed border-collapse text-left">
              {/* Fixed widths so columns line up across every group. */}
              <colgroup>
                <col className="w-10" />
                <col className="w-[34%]" />
                <col />
                <col className="w-[104px]" />
                <col className="w-[104px]" />
              </colgroup>
              <thead>
                <tr className="bg-gray-50 text-[10px] font-bold uppercase tracking-[0.07em] text-gray-400">
                  <th className="py-2 pl-4 pr-1">#</th>
                  <th className="px-3 py-2">Signal</th>
                  <th className="px-3 py-2">Action plan</th>
                  <th className="px-3 py-2">Runtime</th>
                  <th className="px-3 py-2">Review</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {group.items.map(({ definition: d, plan }) => {
                  const rt = draftRuntime(d, draft);
                  const status = STATUS_META[plan.status];
                  return (
                    <tr
                      key={d.id}
                      data-definition-id={d.id}
                      onClick={() => openSignal(d.id, "settings")}
                      className="cursor-pointer align-top hover:bg-brand-faint"
                    >
                      <td className="py-2.5 pl-4 pr-1 font-mono text-[11px] text-gray-400">
                        {d.order}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-[12.5px] font-semibold text-gray-900">
                          {d.title}
                        </div>
                        <div className="mt-0.5 font-mono text-[10.5px] text-gray-400">
                          {d.id}
                        </div>
                        {d.implementation_status !== "implemented" && (
                          <div className="mt-0.5 text-[10.5px] font-semibold text-gray-500">
                            {IMPLEMENTATION_LABEL[d.implementation_status]}
                          </div>
                        )}
                        {d.review !== "Keep" &&
                          !draft.review_resolved[d.id] && (
                            <div className="mt-0.5 text-[10.5px] font-semibold text-purple-700">
                              Catalogue review: {d.review}
                            </div>
                          )}
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10.5px] font-bold ${TIER_META[draftTier(d, draft)].className}`}
                          >
                            {TIER_META[draftTier(d, draft)].label}
                          </span>
                          {d.layers.length > 0 && (
                            <span className="text-[10.5px] text-gray-500">
                              Needs{" "}
                              {d.layers.map((l, i) => (
                                <React.Fragment key={l}>
                                  {i > 0 && ", "}
                                  <span
                                    className={
                                      draft.layers[l] === false
                                        ? "text-red-700 line-through"
                                        : ""
                                    }
                                  >
                                    {layerLabel(l)}
                                  </span>
                                </React.Fragment>
                              ))}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <ol className="m-0 flex list-none flex-col gap-1 p-0">
                          {plan.steps.map((step, i) => {
                            const target = targetText(step);
                            return (
                              <li
                                key={i}
                                className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px]"
                              >
                                {plan.steps.length > 1 && (
                                  <span className="font-mono text-[10.5px] text-gray-400">
                                    {i + 1}.
                                  </span>
                                )}
                                <span className="text-gray-800">
                                  {step.title}
                                </span>
                                <span
                                  className={`${pill} !py-0 ${OWNER_CLASS[step.owner]}`}
                                >
                                  {step.owner === "you"
                                    ? "You"
                                    : registry.action_plan_owners[step.owner]}
                                </span>
                                {target && (
                                  <span
                                    className={`text-[11px] ${
                                      target.available
                                        ? "text-gray-500"
                                        : "text-red-700"
                                    }`}
                                  >
                                    → {target.label}
                                    {!target.available && " (unavailable)"}
                                  </span>
                                )}
                              </li>
                            );
                          })}
                        </ol>
                        {plan.awareness && (
                          <div className="mt-1 text-[10.5px] font-semibold text-sky-800">
                            Awareness · clears only when the data changes
                          </div>
                        )}
                        {plan.status === "changes_requested" && plan.note && (
                          <div className="mt-1 text-[11px] font-semibold text-red-700">
                            Changes requested: {plan.note}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <span
                          className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-bold ring-1 ring-inset ${RUNTIME_META[rt].className}`}
                        >
                          {RUNTIME_META[rt].label}
                        </span>
                        {rt !== d.state.runtime && (
                          <div className="mt-1 text-[10.5px] font-semibold text-amber-700">
                            Unapplied
                          </div>
                        )}
                        <div className="mt-1 text-[11px] tabular-nums text-gray-500">
                          {d.open_firings
                            ? `${d.open_firings} open`
                            : "None open"}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-col items-start gap-1.5">
                          <span className="flex flex-wrap items-center gap-1">
                            <span className={`${pill} ${status.className}`}>
                              {status.label}
                            </span>
                            {plan.staged && (
                              <span className="text-[10.5px] font-semibold text-amber-700">
                                Unapplied
                              </span>
                            )}
                          </span>
                          <span className="flex flex-col items-start gap-1">
                            {plan.status !== "approved" && (
                              <button
                                type="button"
                                onClick={e => {
                                  e.stopPropagation();
                                  approve(d.id, plan);
                                }}
                                className="rounded-md border border-emerald-300 bg-white px-2 py-0.5 text-[11.5px] font-semibold text-emerald-700 hover:bg-emerald-50"
                              >
                                Approve
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={e => {
                                e.stopPropagation();
                                openSignal(d.id, "plan");
                              }}
                              className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11.5px] font-semibold text-gray-600 hover:border-gray-300 hover:text-gray-900"
                            >
                              {plan.status === "approved" ? "Edit" : "Review"}
                            </button>
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      {editing && (
        <RegistrySignalDrawer
          key={editing.definition.id}
          signalId={editing.definition.id}
          initialTab={openTab}
          registry={registry}
          draft={draft}
          appliedPlans={appliedPlans}
          onDraftChange={onDraftChange}
          onSaveReviews={onSaveReviews}
          onClose={() => setEditingId(null)}
          onOpenSignal={id => openSignal(id, "settings")}
        />
      )}
    </div>
  );
};

interface RegistrySignalDrawerProps
  extends Omit<SignalActionsPanelProps, "draft"> {
  draft: SignalAdminDraft;
  signalId: string;
  initialTab?: DrawerTab;
  onClose: () => void;
  /** Switch the drawer to another signal (from a dependency). */
  onOpenSignal: (id: string) => void;
}

/** One signal's Registry page (Signal settings / Card / Action plan) as a
 * drawer — what a Registry row opens, and what other sections (the Rule
 * Classifier) open for a signal. Edits stage into the shared draft. */
export const RegistrySignalDrawer: React.FC<RegistrySignalDrawerProps> = ({
  signalId,
  initialTab = "settings",
  registry,
  draft,
  appliedPlans,
  onDraftChange,
  onSaveReviews,
  onClose,
  onOpenSignal,
}) => {
  const targets = useMemo(
    () => draftTargets(registry, draft),
    [registry, draft]
  );
  const definition = registry.definitions.find(d => d.id === signalId);
  const plan = definition
    ? draftPlan(registry, draft, appliedPlans, signalId)
    : null;
  if (!definition || !plan) return null;
  const stage = (next: ActionPlan | null) => {
    const plans = { ...(draft.action_plans ?? {}) };
    if (next) plans[signalId] = next;
    else delete plans[signalId];
    onDraftChange({ ...draft, action_plans: plans });
  };
  return (
    <PlanEditor
      key={signalId}
      initialTab={initialTab}
      registry={registry}
      definition={definition}
      plan={plan}
      targets={targets}
      hasStoredPlan={
        Boolean(draft.action_plans?.[signalId]) ||
        Boolean(appliedPlans[signalId])
      }
      draft={draft}
      onDraftChange={onDraftChange}
      onClose={onClose}
      onOpenSignal={onOpenSignal}
      onStage={next => {
        stage(next);
        onClose();
      }}
      liveApproved={appliedPlans[signalId]?.status === "approved"}
      onSaveReview={async next => {
        stage(next);
        await onSaveReviews({ [signalId]: next });
        onClose();
      }}
    />
  );
};

/** Every field that reaches users has a number, in editor order: each
 * sub-action's title, owner, page and detail, then the awareness flag.
 * The preview shows the same numbers where each field ends up. */
type FieldPart = "title" | "owner" | "page" | "detail";
const FIELD_PARTS: FieldPart[] = ["title", "owner", "page", "detail"];
const PART_LABEL: Record<FieldPart, string> = {
  title: "title",
  owner: "owner",
  page: "where in ExecBridge",
  detail: "detail",
};
const fieldNo = (step: number, part: FieldPart) =>
  step * FIELD_PARTS.length + FIELD_PARTS.indexOf(part) + 1;
const awarenessNo = (steps: number) => steps * FIELD_PARTS.length + 1;
const fieldId = (no: number) => `plan-field-${no}`;

const FieldTag: React.FC<{ no: number }> = ({ no }) => (
  <span className="mr-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-400 px-1 align-middle text-[9.5px] font-bold not-italic text-gray-900">
    {no}
  </span>
);

const FieldMarker: React.FC<{
  no: number;
  label: string;
  onJump: (no: number) => void;
}> = ({ no, label, onJump }) => (
  <button
    type="button"
    title={`Edit ${label}`}
    aria-label={`Edit field ${no}: ${label}`}
    onClick={e => {
      e.stopPropagation();
      onJump(no);
    }}
    className="pointer-events-auto ml-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-amber-400 px-1 align-middle text-[10px] font-bold not-italic text-gray-900 shadow ring-2 ring-white hover:bg-amber-500"
  >
    {no}
  </button>
);

/** The plan as the step-by-step view users would get: their own steps
 * link to the page; a step someone else owns says who, with no link. */
const planAsUserGuide = (
  definition: SignalDefinition,
  steps: ActionPlanStep[],
  awareness: boolean,
  registry: SignalAdminRegistry,
  targets: ReturnType<typeof draftTargets>
): SignalGuide => ({
  title: definition.title,
  why: "",
  clears_when: awareness ? USER_AWARENESS_NOTE : "",
  steps: steps.map(step => {
    const record = targets.find(t => t.id === step.target);
    const yours = step.owner === "you";
    const who = yours
      ? ""
      : `${registry.action_plan_owners[step.owner] ?? step.owner} does this.`;
    return {
      title: step.title,
      body: [who, step.detail].filter(Boolean).join(" "),
      owner: step.owner,
      owner_label: registry.action_plan_owners[step.owner] ?? step.owner,
      link:
        yours && record && record.available && record.kind
          ? {
              target: record.id,
              label: record.label,
              kind: record.kind,
              path: record.kind === "path" ? record.route : null,
            }
          : null,
    };
  }),
});

const UserPreview: React.FC<{
  registry: SignalAdminRegistry;
  definition: SignalDefinition;
  steps: ActionPlanStep[];
  awareness: boolean;
  targets: ReturnType<typeof draftTargets>;
  onJump: (no: number) => void;
  /** Parameter values for {{parameter-id}} in the previewed text. */
  parameters: Record<string, number | string>;
  tier: SignalTier;
}> = ({
  registry,
  definition,
  steps,
  awareness,
  targets,
  onJump,
  parameters,
  tier,
}) => {
  if (!steps.length) return null;
  const bucket = bucketOf(steps);
  const single = bucket === "single_independent";
  const handOff = bucket === "single_dependent";
  const page = targets.find(t => t.id === steps[0].target);
  const { label, detail, ownIndex } = planCardCta(steps);
  const mark = (i: number, part: FieldPart) => (
    <FieldMarker
      no={fieldNo(i, part)}
      label={`sub-action ${i + 1} ${PART_LABEL[part]}`}
      onJump={onJump}
    />
  );
  const awarenessMark = awareness ? (
    <FieldMarker
      no={awarenessNo(steps.length)}
      label="awareness signal"
      onJump={onJump}
    />
  ) : undefined;
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-gray-500">
          What users would see
        </span>
        <span className="text-[11px] text-gray-400">
          Example values · links inactive
        </span>
      </div>
      <p className="m-0 text-[11.5px] text-amber-700">
        Preview of the card once action plans drive it. Until then users see
        today&apos;s card and guide.
      </p>
      <p className="m-0 text-[11.5px] text-gray-500">
        Each number matches a field above — click it to edit that field.
      </p>
      <CardPreview
        definition={definition}
        tier={tier}
        parameters={parameters}
        label={label}
        awarenessNote={awareness ? USER_AWARENESS_NOTE : null}
        detail={detail}
        markers={{
          cta: single
            ? mark(0, "title")
            : handOff
              ? mark(ownIndex, "title")
              : undefined,
          detail: single && steps[0].detail ? mark(0, "detail") : undefined,
          awareness: awarenessMark,
        }}
      />
      {single ? (
        <p className="m-0 text-[12px] text-gray-600">
          The button opens{" "}
          <strong className="text-gray-900">
            {page?.label ?? "no page — pick one above"}
          </strong>
          {mark(0, "page")} · the card&apos;s own user acts
          {mark(0, "owner")}
        </p>
      ) : (
        <>
          <p className="m-0 text-[12px] text-gray-600">
            {handOff
              ? "The button opens the steps, so the user also sees what others do next:"
              : "The button opens the steps:"}
          </p>
          <div className="pointer-events-none overflow-hidden rounded-2xl border border-gray-200 shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
            <SignalGuideContent
              preview
              planBucket={bucket}
              guide={planAsUserGuide(
                definition,
                steps,
                awareness,
                registry,
                targets
              )}
              accountName="Example account"
              signalTitle={definition.title}
              tier={definition.tier}
              fields={{
                ...parameters,
                ...sampleFields(definition.id),
              }}
              markers={{
                step: (i, part) =>
                  part === "title" ? (
                    <>
                      {mark(i, "title")}
                      {mark(i, "owner")}
                    </>
                  ) : part === "body" ? (
                    steps[i]?.detail ? (
                      mark(i, "detail")
                    ) : null
                  ) : (
                    mark(i, "page")
                  ),
                clears: awarenessMark,
              }}
            />
          </div>
        </>
      )}
    </section>
  );
};

type DrawerTab = "settings" | "card" | "plan";

interface PlanEditorProps {
  initialTab: DrawerTab;
  registry: SignalAdminRegistry;
  definition: SignalDefinition;
  plan: DraftPlan;
  targets: ReturnType<typeof draftTargets>;
  hasStoredPlan: boolean;
  draft: SignalAdminDraft;
  onDraftChange: (next: SignalAdminDraft) => void;
  onClose: () => void;
  /** Switch the drawer to another signal (from a dependency). */
  onOpenSignal: (id: string) => void;
  /** null = back to ExecBridge's proposal. */
  onStage: (plan: ActionPlan | null) => void;
  /** The applied plan is approved, so it is on users' cards. */
  liveApproved: boolean;
  /** Save a changes-requested review straight away for every admin. */
  onSaveReview: (plan: ActionPlan) => Promise<void>;
}

const PlanEditor: React.FC<PlanEditorProps> = ({
  initialTab,
  registry,
  definition,
  plan,
  targets,
  hasStoredPlan,
  draft,
  onDraftChange,
  onClose,
  onOpenSignal,
  onStage,
  liveApproved,
  onSaveReview,
}) => {
  const [tab, setTab] = useState<DrawerTab>(initialTab);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [steps, setSteps] = useState<ActionPlanStep[]>(
    plan.steps.map(s => ({ ...s }))
  );
  const [rationale, setRationale] = useState(plan.rationale);
  const [note, setNote] = useState(plan.note);
  const [awareness, setAwareness] = useState(plan.awareness);
  const [tried, setTried] = useState<ActionPlan["status"] | null>(null);
  const [flash, setFlash] = useState<number | null>(null);

  /** From a preview marker: bring its field into view, focus and flash it. */
  const jump = (no: number) => {
    const el = document.getElementById(fieldId(no));
    el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    el?.focus({ preventScroll: true });
    setFlash(no);
    window.setTimeout(() => setFlash(f => (f === no ? null : f)), 1600);
  };
  const fieldClass = (no: number) =>
    `${inputClass} transition-shadow ${flash === no ? "ring-2 ring-amber-400" : ""}`;

  const bucket = steps.length ? bucketOf(steps) : null;
  const edited = isEditedFromProposal(registry, definition.id, steps);
  const build = (status: ActionPlan["status"]): ActionPlan => ({
    steps: steps.map(s => ({
      ...s,
      title: s.title.trim(),
      detail: s.detail.trim(),
    })),
    rationale: rationale.trim(),
    status,
    note: status === "approved" ? "" : note.trim(),
    awareness,
  });
  const errors = tried ? planErrors(build(tried)) : [];

  const submit = async (status: ActionPlan["status"]) => {
    setTried(status);
    setSaveError("");
    const next = build(status);
    if (planErrors(next).length) return;
    // A review saves for every admin at once; approvals (and withdrawing a
    // live approved plan) change users' cards, so they wait for Apply.
    if (status === "changes_requested" && !liveApproved) {
      setSaving(true);
      try {
        await onSaveReview(next);
      } catch (error) {
        setSaveError(
          typeof error === "string" ? error : "Couldn't save this review."
        );
      } finally {
        setSaving(false);
      }
      return;
    }
    onStage(next);
  };

  const setStep = (i: number, patch: Partial<ActionPlanStep>) =>
    setSteps(prev => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const move = (i: number, by: -1 | 1) =>
    setSteps(prev => {
      const next = [...prev];
      const [step] = next.splice(i, 1);
      next.splice(i + by, 0, step);
      return next;
    });

  return (
    <Drawer
      open
      onClose={onClose}
      eyebrow={
        <span className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-brand-dark">
          Signal
        </span>
      }
      title={definition.title}
      subtitle={
        <>
          <span className="block font-mono">{definition.id}</span>
          <span className="block">{definition.trigger}</span>
        </>
      }
    >
      <div
        className="-mt-2 mb-4 flex gap-1 border-b border-gray-200"
        role="tablist"
        aria-label="Signal"
      >
        {(
          [
            ["settings", "Signal settings"],
            ["card", "Card"],
            ["plan", "Action plan"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`-mb-px border-b-2 px-3 py-2 text-[12.5px] font-semibold ${
              tab === id
                ? "border-brand text-brand-dark"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "card" ? (
        <div className="flex flex-col gap-4">
          <p className="m-0 rounded-md bg-gray-50 px-3 py-2 text-[12px] text-gray-600">
            {plan.status === "approved"
              ? "This signal's action plan is approved, so the card's button comes from the plan. The card text below still applies."
              : "Card text and the button users see while this signal's action plan isn't approved."}
          </p>
          <CtaEditor
            embedded
            definition={definition}
            registry={registry}
            draft={draft}
            onDraftChange={onDraftChange}
            onClose={() => undefined}
          />
        </div>
      ) : tab === "settings" ? (
        <SignalSettingsContent
          signalId={definition.id}
          registry={registry}
          draft={draft}
          onDraftChange={onDraftChange}
          onOpenSignal={onOpenSignal}
        />
      ) : (
        <div className="flex flex-col gap-4 text-[12.5px]">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-gray-500">Bucket</span>
            <span className="rounded-full bg-gray-900 px-2.5 py-0.5 text-[11px] font-semibold text-white">
              {bucket ? registry.action_plan_buckets[bucket]?.label : "—"}
            </span>
            <span className={`${pill} ${STATUS_META[plan.status].className}`}>
              {STATUS_META[plan.status].label}
            </span>
            {edited && (
              <span className="text-[11px] font-semibold text-amber-700">
                Edited from ExecBridge&apos;s proposal
              </span>
            )}
          </div>
          {bucket && (
            <p className="m-0 text-[11.5px] text-gray-500">
              {registry.action_plan_buckets[bucket]?.hint} The bucket updates as
              you change sub-actions and owners.
            </p>
          )}

          <ol className="m-0 flex list-none flex-col gap-3 p-0">
            {steps.map((step, i) => (
              <li
                key={i}
                className="flex flex-col gap-2 rounded-lg border border-gray-200 p-3"
              >
                <span className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-gray-400">
                  Sub-action {i + 1}
                </span>
                <div className="flex items-center gap-1">
                  <FieldTag no={fieldNo(i, "title")} />
                  <input
                    id={fieldId(fieldNo(i, "title"))}
                    aria-label={`Sub-action ${i + 1} title`}
                    value={step.title}
                    maxLength={100}
                    onChange={e => setStep(i, { title: e.target.value })}
                    className={fieldClass(fieldNo(i, "title"))}
                    placeholder="What has to happen"
                  />
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px] font-semibold text-gray-500">
                      <FieldTag no={fieldNo(i, "owner")} />
                      Owner
                    </span>
                    <select
                      id={fieldId(fieldNo(i, "owner"))}
                      value={step.owner}
                      onChange={e =>
                        setStep(i, { owner: e.target.value as ActionOwner })
                      }
                      className={fieldClass(fieldNo(i, "owner"))}
                    >
                      {(
                        Object.keys(
                          registry.action_plan_owners
                        ) as ActionOwner[]
                      ).map(owner => (
                        <option key={owner} value={owner}>
                          {registry.action_plan_owners[owner]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px] font-semibold text-gray-500">
                      <FieldTag no={fieldNo(i, "page")} />
                      Where in ExecBridge
                    </span>
                    <select
                      id={fieldId(fieldNo(i, "page"))}
                      value={step.target}
                      onChange={e => setStep(i, { target: e.target.value })}
                      className={fieldClass(fieldNo(i, "page"))}
                    >
                      <option value="">Outside ExecBridge</option>
                      {targets.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.label}
                          {t.available ? "" : " (unavailable)"}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="flex items-center">
                  <FieldTag no={fieldNo(i, "detail")} />
                  <input
                    id={fieldId(fieldNo(i, "detail"))}
                    aria-label={`Sub-action ${i + 1} detail`}
                    value={step.detail}
                    maxLength={300}
                    onChange={e => setStep(i, { detail: e.target.value })}
                    className={fieldClass(fieldNo(i, "detail"))}
                    placeholder="Detail — shown to users (optional)"
                  />
                </div>
                <div className="flex gap-3 text-[11.5px] font-semibold">
                  <button
                    type="button"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                    className="text-gray-500 hover:text-gray-900 disabled:opacity-40"
                  >
                    Move up
                  </button>
                  <button
                    type="button"
                    disabled={i === steps.length - 1}
                    onClick={() => move(i, 1)}
                    className="text-gray-500 hover:text-gray-900 disabled:opacity-40"
                  >
                    Move down
                  </button>
                  <button
                    type="button"
                    disabled={steps.length === 1}
                    onClick={() =>
                      setSteps(prev => prev.filter((_, j) => j !== i))
                    }
                    className="ml-auto text-red-600 hover:text-red-800 disabled:opacity-40"
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ol>
          {steps.length < MAX_STEPS && (
            <button
              type="button"
              onClick={() => setSteps(prev => [...prev, blankStep()])}
              className="self-start text-[12px] font-semibold text-brand hover:underline"
            >
              + Add sub-action
            </button>
          )}

          <label
            className={`flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 transition-shadow ${
              flash === awarenessNo(steps.length) ? "ring-2 ring-amber-400" : ""
            }`}
          >
            <FieldTag no={awarenessNo(steps.length)} />
            <input
              id={fieldId(awarenessNo(steps.length))}
              type="checkbox"
              checked={awareness}
              onChange={e => setAwareness(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-[12px] text-sky-900">
              <strong>Awareness signal.</strong>{" "}
              {registry.action_plan_awareness_disclaimer.replace(
                /^Awareness signal:\s*(\w)/,
                (_, first: string) => first.toUpperCase()
              )}
            </span>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold text-gray-500">
              Why this bucket{" "}
              <span className="font-normal text-gray-400">
                · Admin only, not shown to users
              </span>
            </span>
            <textarea
              value={rationale}
              maxLength={400}
              rows={2}
              onChange={e => setRationale(e.target.value)}
              className={inputClass}
            />
          </label>
          <UserPreview
            registry={registry}
            definition={definition}
            steps={steps}
            awareness={awareness}
            targets={targets}
            onJump={jump}
            parameters={draftParameterValues(registry, draft)}
            tier={draftTier(definition, draft)}
          />

          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold text-gray-500">
              Review note (required to request changes){" "}
              <span className="font-normal text-gray-400">
                · Admin only, not shown to users
              </span>
            </span>
            <textarea
              value={note}
              maxLength={300}
              rows={2}
              onChange={e => setNote(e.target.value)}
              className={inputClass}
            />
          </label>

          {(errors.length > 0 || saveError) && (
            <ul className="m-0 list-disc rounded-md bg-red-50 py-2 pl-6 pr-3 text-[12px] text-red-700">
              {errors.map(e => (
                <li key={e}>{e}</li>
              ))}
              {saveError && <li>{saveError}</li>}
            </ul>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              disabled={saving}
              onClick={() => void submit("approved")}
            >
              Approve
            </Button>
            <Button
              variant="secondary"
              disabled={saving}
              onClick={() => void submit("changes_requested")}
            >
              {saving ? "Saving…" : "Request changes"}
            </Button>
            {hasStoredPlan && (
              <Button variant="secondary" onClick={() => onStage(null)}>
                Back to ExecBridge proposal
              </Button>
            )}
          </div>
          <p className="m-0 text-[11.5px] text-gray-400">
            Approving stages this plan; it goes out with the next Apply.{" "}
            {liveApproved
              ? "This plan is live, so requesting changes withdraws it from users' cards — that also goes out with the next Apply."
              : "Requesting changes saves straight away for every admin; it doesn't change what users see."}
          </p>
        </div>
      )}
    </Drawer>
  );
};

export default SignalActionsPanel;
